import React, { useState } from "react";
import { Plus, Printer } from "lucide-react";
import SplitReceiptModal from "./SplitReceiptModal";
import { getReceiptConfig, setReceiptConfig } from "../utils/receiptConfig";

export default function SplitReceiptConfig({ config, onChange }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);

  // Fallback defaults
  const splitOrderReceipts = config.splitOrderReceipts || [
    { id: "checker", name: "Nota Checker", categories: "ALL", printerMac: "", printerName: "" },
    { id: "dapur", name: "Nota Dapur", categories: [], printerMac: "", printerName: "" },
    { id: "bar", name: "Nota Bar", categories: [], printerMac: "", printerName: "" },
  ];

  const handleDirectSave = (patch) => {
    // Update local UI state
    onChange(patch);
    // Save directly to localStorage
    const currentConfig = getReceiptConfig();
    setReceiptConfig({ ...currentConfig, ...patch });
  };

  const handleToggle = () => {
    handleDirectSave({
      splitOrderEnabled: !config.splitOrderEnabled,
      splitOrderReceipts: splitOrderReceipts,
    });
  };

  const handleAdd = () => {
    setEditingMapping({
      id: Date.now().toString(),
      name: "",
      categories: [],
      printerMac: "",
      printerName: "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (mapping) => {
    setEditingMapping({ ...mapping });
    setIsModalOpen(true);
  };

  const handleSave = (savedMapping) => {
    const isExisting = splitOrderReceipts.some((m) => m.id === savedMapping.id);
    let newReceipts = [];
    if (isExisting) {
      newReceipts = splitOrderReceipts.map((m) =>
        m.id === savedMapping.id ? savedMapping : m
      );
    } else {
      newReceipts = [...splitOrderReceipts, savedMapping];
    }
    handleDirectSave({ splitOrderReceipts: newReceipts });
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    if (id === "checker") return;
    const newReceipts = splitOrderReceipts.filter((m) => m.id !== id);
    handleDirectSave({ splitOrderReceipts: newReceipts });
  };

  return (
    <div className="mt-6 bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-teal-50 ring-1 ring-teal-200/70 flex items-center justify-center shrink-0">
            <Printer className="w-3.5 h-3.5 text-teal-600" />
          </div>
          <h4 className="text-[11px] font-bold text-teal-600 uppercase tracking-wider">
            Split Nota Pesanan
          </h4>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!config.splitOrderEnabled}
            onChange={handleToggle}
          />
          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
        </label>
      </div>
      <div className="text-[11px] text-slate-500 mb-4 pb-4 border-b border-slate-100">
        Aktifkan untuk memisahkan cetak nota pesanan berdasarkan kategori sesuai kebutuhan.
      </div>

      {config.splitOrderEnabled && (
        <div className="space-y-4">
          {splitOrderReceipts.map((receipt) => (
            <div
              key={receipt.id}
              className="bg-slate-50 ring-1 ring-slate-200/60 rounded-xl p-4 transition-colors hover:bg-slate-100 cursor-pointer"
              onClick={() => handleEdit(receipt)}
            >
              <div className="flex justify-between items-start mb-2">
                <h5 className="font-semibold text-slate-800 text-sm">
                  {receipt.name}
                </h5>
                <span className="text-xs font-semibold text-teal-600">
                  Mapping Kategori
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {receipt.categories === "ALL" ? (
                  <span className="px-2 py-1 bg-teal-600 text-white text-[10px] font-semibold rounded">
                    Semua Kategori
                  </span>
                ) : receipt.categories && receipt.categories.length > 0 ? (
                  receipt.categoryNames?.map((cat, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-teal-600 text-white text-[10px] font-semibold rounded"
                    >
                      {cat}
                    </span>
                  )) || (
                    <span className="px-2 py-1 bg-teal-600 text-white text-[10px] font-semibold rounded">
                      {receipt.categories.length} Kategori Dipilih
                    </span>
                  )
                ) : (
                  <span className="px-2 py-1 bg-slate-100 border border-slate-200 text-slate-500 text-[10px] font-semibold rounded">
                    Belum ada kategori (Tidak dicetak)
                  </span>
                )}
              </div>
              <div className="bg-white rounded-lg p-3 flex items-center justify-between border border-slate-200">
                <div className="flex items-center gap-2">
                  <Printer className="w-4 h-4 text-slate-500" />
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-400 font-semibold leading-tight">
                      Printer Tujuan
                    </span>
                    <span className="text-xs font-bold text-slate-700 leading-tight">
                      {receipt.printerName || "Belum dipilih"}
                    </span>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 flex flex-col items-end gap-1">
                  <span>
                    {receipt.printerMac
                      ? "Bluetooth \u00B7 " + receipt.printerName
                      : "Bluetooth \u00B7 Printer Default"}
                  </span>
                  <span className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 text-slate-600 rounded text-[9px] font-bold">
                    {receipt.printerWidth || "58mm"}
                  </span>
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={handleAdd}
            className="w-full py-3 border-2 border-dashed border-teal-300 rounded-xl text-teal-600 font-semibold text-xs flex items-center justify-center gap-2 hover:bg-teal-50 hover:border-teal-400 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nota Pesanan
          </button>
        </div>
      )}

      {isModalOpen && (
        <SplitReceiptModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={editingMapping}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
